import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import { concatMap, delay, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { AuthService } from 'src/app/auth/auth.service';
import { SampleVisualService, UserDatasetService, UserVisualDto, UserVisualService, VisualService } from 'src/app/services/api.services';
import { FileDownloaderService } from 'src/app/services/file-downloader.service';
import { SnackService } from 'src/app/services/snack.service';
import { DeleteFormattingOptions, getDeleteDialogData, getDeleteDialogDataVisualAndUsage } from 'src/app/shared/utils/deleteMessage';
import { VisualsActions, VisualsSelectors } from '.';
import { GenericAlertButton, GenericAlertDialog } from '../../../shared/dialogs';
import { catchAppError, escapeHtml } from '../../../shared/utils';
import { AppSelectors } from '../../../state/app';
import { VisualImportComponent, VisualImportResult } from '../../components/visual-import/visual-import.component';
import { VisualListItem, VisualsCategory } from './reducer';

@Injectable()
export class VisualsEffects {
  constructor(
    private store: Store,
    private actions$: Actions,
    private router: Router,
    private snackService: SnackService,
    private dialog: MatDialog,
    private visualService: VisualService,
    private userVisualService: UserVisualService,
    private userDatasetService: UserDatasetService,
    private sampleVisualService: SampleVisualService,
    private authService: AuthService,
    private fileDownloader: FileDownloaderService) { }


  expireCache$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.expireCache),
    mergeMap(({ visualIds }) => {
      let requests = visualIds.map(v => this.visualService.expireCache(v));
      return forkJoin(requests).pipe(
        catchAppError(),
        map(() => VisualsActions.expireCacheCompleted({ visualIds })),
      );
    }))
  );



  getVisuals$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.getVisuals),
    withLatestFrom(this.store.select(AppSelectors.getUser)),
    switchMap(([{ }, user]) => this.visualService.listVisuals({
        includeLibrary: user.roles.isAdmin,
        includeSamples: true,
        includeShared: true,
        includeContent: false
      }).pipe(
      map((visuals) => VisualsActions.getVisualsSuccess({ visuals })),
      catchAppError(error => {
        return of(VisualsActions.getVisualsFailure({ error }));
      })
    )))
  );

  addVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.addVisual),
    tap(() => this.router.navigate(['visuals', 'add'])),
  ), { dispatch: false });

  editVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.editVisual),
    tap(({ visualId }) => this.router.navigate(['visuals', visualId])),
  ), { dispatch: false });

  viewVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.viewVisual),
    tap(({ visualId }) => this.router.navigate(['visuals', visualId, 'view'])),
  ), { dispatch: false });

  addScheduleVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.addScheduleVisual),
    tap(({ visualId }) => this.router.navigate(['scheduler/add/visual', visualId], { queryParams: { returnUrl: '/visuals' } })),
  ), { dispatch: false });

  copyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.copyVisuals),
    concatMap(({ visuals }) => {
      let requests = visuals.map(v => this.visualService.copyVisual(v));
      return forkJoin(requests).pipe(
        map((visuals) => VisualsActions.copyVisualsSuccess({ visuals })),
        catchAppError(error => {
          return of(VisualsActions.copyVisualsFailure({ error }));
        })
      );
    })
  ));

  selectAfterCopyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.copyVisualsSuccess),
    filter(({ visuals }) => visuals.length > 1),
    concatMap(({ visuals }) => [
      VisualsActions.setSelectedVisualIds({ selectedVisualIds: visuals.map(v => v.id) }),
      VisualsActions.getVisuals(),
      VisualsActions.updateDisplayParams({ displayParams: { category: VisualsCategory.User } }),
    ]))
  );

  // Navigate to edit view of the copy visual
  navigateAfterCopyVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.copyVisualsSuccess),
    filter(({ visuals }) => visuals.length == 1),
    map(({ visuals }) => VisualsActions.editVisual({ visualId: visuals[0].id })))
  );


  showDeleteVisualDialog$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.showDeleteVisualDialog),
    withLatestFrom(
      this.store.select(VisualsSelectors.getVisuals),
      this.store.select(AppSelectors.getUser)),
    switchMap(([{ visualIds }, visuals, user]) => {
      let visualsToDelete = visuals.filter(v => visualIds.includes(v.id));
      let usageRequests = visualsToDelete.map(v => this.visualService.getUsage(v.id));

      return forkJoin(usageRequests).pipe(
        tap(usages => {
          let dialogRef: MatDialogRef<GenericAlertDialog>;

          if (visualsToDelete.length > 1) {
            visualsToDelete = [...visualsToDelete];
            visualsToDelete.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

            dialogRef = this.dialog.open(GenericAlertDialog, {
              width: '560px',
              data: {
                title: 'Alert',
                content: `
                  <p>Are you sure you want to delete the following pivots?</p>
                  <p>${visualsToDelete.map(v => escapeHtml(v.name)).join(', ')}</p>
                `,
                buttons: [GenericAlertButton.cancel, GenericAlertButton.delete]
              }
            });
          }
          else {
            dialogRef = this.dialog.open(GenericAlertDialog, {
              width: '560px',
              data: getDeleteDialogDataVisualAndUsage(visualsToDelete[0], usages[0])
            });
          }

          return dialogRef.afterClosed().pipe(
            tap((result) => {
              if (!result) {
                return;
              }

              this.store.dispatch(VisualsActions.deleteVisuals({ visualIds: visualsToDelete.map(v => v.id) }));
            })
          ).subscribe();
        })
      );
    })),
    { dispatch: false }
  );

  deleteVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.deleteVisuals),
    switchMap(({ visualIds }) => {
      let requests = visualIds.map(id => this.visualService.markAsDeleted(id));
      return forkJoin(requests).pipe(
        map(() => VisualsActions.deleteVisualsSuccess({ visualIds })),
        catchAppError(error => {
          return of(VisualsActions.deleteVisualsFailure({ error }));
        })
      );
    })
  ));

  updateUserVisual$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.updateUserVisual),
    concatMap(({ visual }) => {
      const userVisual: UserVisualDto = {
        visualId: visual.id,
        favourite: visual.favourite,
        home: visual.home
      };
      return this.userVisualService.updateUserVisual(userVisual).pipe(
        map(() => VisualsActions.updateUserVisualSuccess({ userVisual })),
        catchAppError(error => {
          return of(VisualsActions.updateUserVisualFailure({ error }));
        })
      )
    })
  ));


  toggleVisualLibrary$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.toggleVisualLibrary),
    withLatestFrom(this.store.select(VisualsSelectors.getVisuals)),
    switchMap(([{ visualId }, visuals]) => {
      let visual = visuals.find(v => v.id == visualId);

      return this.visualService.setFromLibrary(visual.id, !visual.fromLibrary).pipe(
        map((fromLibrary) => VisualsActions.toggleVisualLibrarySuccess({ visualId: visual.id, fromLibrary })),
        catchAppError(error => {
          return of(VisualsActions.toggleVisualLibraryFailure({ error }))
        }));
    })
  ));


  toggleVisualIsSample$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.toggleVisualIsSample),
    withLatestFrom(this.store.select(VisualsSelectors.getVisuals),),
    switchMap(([{ visualId }, visuals]) => {
      let sample = !visuals.find(v => v.id == visualId).sample;

      return this.sampleVisualService.setVisualAsSample(visualId, sample).pipe(
        map(() => VisualsActions.toggleVisualIsSampleSuccess({ visualId, sample })),
        catchAppError(error => {
          return of(VisualsActions.toggleVisualIsSampleFailure({ error }));
        })
      );
    }))
  );

  deleteSampleForUser$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.deleteSampleForUser),
    switchMap(({ visualId }) => {
      return this.sampleVisualService.deleteSampleForUser(visualId).pipe(
        map(() => VisualsActions.deleteSampleForUserSuccess({ visualId })),
        catchAppError(error => {
          return of(VisualsActions.deleteSampleForUserFailure({ error }));
        })
      );
    }))
  );

  reloadAfterDeleteSampleForUser$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.deleteSampleForUserSuccess),
    tap(() => this.store.dispatch(VisualsActions.getVisuals()))
  ), { dispatch: false });


  openImportDialog$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.openImportDialog),
    tap(() => {
      this.dialog.open(VisualImportComponent, {
        ...VisualImportComponent.dialogConfig
      }).afterClosed().subscribe((result: VisualImportResult) => {
        if (result?.visualId) {
          this.store.dispatch(VisualsActions.getVisuals());
          this.store.dispatch(VisualsActions.setSelectedVisualIds({ selectedVisualIds: [result.visualId] }));
        }
      });
    })
  ), { dispatch: false });

  exportDefinition$ = createEffect(() => this.actions$.pipe(
    ofType(VisualsActions.exportDefinition),
    withLatestFrom(this.store.select(VisualsSelectors.getVisuals)),
    concatMap(([{ visualIds }, visuals]) => {
      let visualById = visuals.reduce((m, v) => m.set(v.id, v), new Map<number, VisualListItem>());
      let visualsToExport = visualIds.map(id => visualById.get(id)).filter(v => !!v);

      return visualsToExport.map(v => {
        let url = `/api/Visual/ExportVisualDefinition/${v.id}?access_token=${this.authService.accessToken}`;
        let fileName = `${v.name}.json`;
        this.fileDownloader.downloadUrl(url, fileName);
        return delay(50);
      });
    })),

    { dispatch: false }
  );
}
